import React, { useEffect, useState } from 'react';
import { StatusBar } from 'expo-status-bar';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { ThemeProvider, useTheme } from '@theme/ThemeProvider';
import { RootNavigator } from '@navigation/RootNavigator';
import { usePreferencesStore } from '@stores/preferencesStore';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
} from 'react-native';
import './src/i18n';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

interface PasswordGateProps {
  password: string;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void;
  error: string;
}

const PasswordGate: React.FC<PasswordGateProps> = ({ password, onPasswordChange, onSubmit, error }) => {
  const theme = useTheme();

  return (
    <SafeAreaView style={[passwordStyles.safeArea, { backgroundColor: theme.colors.background }]}>
      <View style={passwordStyles.container}>
        <View
          style={[
            passwordStyles.card,
            { backgroundColor: theme.colors.surface, borderRadius: theme.radius.lg, ...theme.elevation.sm },
          ]}
        >
          <Text style={[passwordStyles.title, { color: theme.colors.textPrimary }]}>Member Companion</Text>
          <Text style={[passwordStyles.subtitle, { color: theme.colors.textSecondary }]}>Enter the access password to continue.</Text>
          <TextInput
            value={password}
            onChangeText={(value) => onPasswordChange(value)}
            placeholder="Password"
            placeholderTextColor={theme.colors.textTertiary}
            secureTextEntry
            autoCapitalize="none"
            autoCorrect={false}
            style={[passwordStyles.input, { borderColor: theme.colors.border, color: theme.colors.textPrimary }]}
            onSubmitEditing={onSubmit}
            textContentType="password"
          />
          {error ? <Text style={[passwordStyles.error, { color: theme.colors.error }]}>{error}</Text> : null}
          <TouchableOpacity
            onPress={onSubmit}
            accessibilityRole="button"
            style={[passwordStyles.button, { backgroundColor: theme.colors.brandPrimary }]}
          >
            <Text style={[passwordStyles.buttonText, { color: theme.colors.textOnPrimary }]}>Unlock</Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
};

export default function App() {
  const { loadPreferences } = usePreferencesStore();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = window.sessionStorage?.getItem('member-companion-auth');
      if (stored === 'true') {
        setIsAuthenticated(true);
      }
    }
  }, []);

  const handlePasswordChange = (value: string) => {
    setPasswordInput(value);
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleUnlock = () => {
    if (passwordInput.trim() === 'cscs') {
      setIsAuthenticated(true);
      setPasswordInput('');
      setErrorMessage('');
      if (typeof window !== 'undefined') {
        window.sessionStorage?.setItem('member-companion-auth', 'true');
      }
    } else {
      setErrorMessage('Incorrect password. Please try again.');
    }
  };

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <StatusBar style="dark" />
          {isAuthenticated ? (
            <RootNavigator />
          ) : (
            <PasswordGate
              password={passwordInput}
              onPasswordChange={handlePasswordChange}
              onSubmit={handleUnlock}
              error={errorMessage}
            />
          )}
        </ThemeProvider>
      </QueryClientProvider>
    </GestureHandlerRootView>
  );
}

const passwordStyles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
  },
  input: {
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
  },
  button: {
    marginTop: 24,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  error: {
    marginTop: 12,
    textAlign: 'center',
    fontSize: 14,
  },
});
